<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import BoardsAPI from 'kanban/api/boards.js';

const { t } = useI18n();

const props = defineProps({
  actionName: { type: String, default: '' },
  modelValue: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update:modelValue']);

const boards = ref([]);
const steps = ref([]);
const loadingSteps = ref(false);

const boardOptions = computed(() =>
  boards.value.map(b => ({ id: b.id, name: b.name }))
);
const stepOptions = computed(() =>
  steps.value.map(s => ({ id: s.id, name: s.name }))
);

const selectedBoard = computed(() =>
  boardOptions.value.find(b => b.id === props.modelValue?.board_id) ?? null
);
const selectedStep = computed(() =>
  stepOptions.value.find(s => s.id === props.modelValue?.step_id) ?? null
);

async function loadBoards() {
  try {
    const { data } = await BoardsAPI.get();
    boards.value = data?.boards ?? [];
  } catch {
    boards.value = [];
  }
}

async function loadSteps(boardId) {
  if (!boardId) { steps.value = []; return; }
  loadingSteps.value = true;
  try {
    const { data } = await BoardsAPI.getOne(boardId);
    steps.value = data?.steps ?? [];
  } catch {
    steps.value = [];
  } finally {
    loadingSteps.value = false;
  }
}

watch(
  () => props.modelValue?.board_id,
  boardId => {
    if (props.actionName === 'move_to_step') loadSteps(boardId);
  },
  { immediate: true }
);

onMounted(loadBoards);

function onBoardSelect(val) {
  const update = { board_id: val?.id ?? null, board_name: val?.name ?? null };
  if (props.actionName === 'move_to_step') {
    update.step_id = null;
    update.step_name = null;
  }
  emit('update:modelValue', update);
}

function onStepSelect(val) {
  emit('update:modelValue', {
    ...props.modelValue,
    step_id: val?.id ?? null,
    step_name: val?.name ?? null,
  });
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="multiselect-wrap--small flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.KANBAN_ACTION.BOARD') }}</span>
      <MultiselectDropdown
        :options="boardOptions"
        :selected-item="selectedBoard"
        :multiselector-placeholder="t('FLOW_BUILDER.ACTION_FORMS.KANBAN_ACTION.SELECT_BOARD')"
        :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
        :has-thumbnail="false"
        @select="onBoardSelect"
      />
    </div>

    <div v-if="actionName === 'move_to_step'" class="multiselect-wrap--small flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.KANBAN_ACTION.STEP') }}</span>
      <MultiselectDropdown
        :options="stepOptions"
        :selected-item="selectedStep"
        :multiselector-placeholder="loadingSteps ? t('FLOW_BUILDER.COMMON.LOADING') : t('FLOW_BUILDER.ACTION_FORMS.KANBAN_ACTION.SELECT_STEP')"
        :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
        :has-thumbnail="false"
        @select="onStepSelect"
      />
    </div>
  </div>
</template>
